<template>
  <div ref="container" class="chart-container" style="position: relative">
    <transition
      name="fade"
      @before-enter="transitionInProgress = true"
      @after-leave="transitionInProgress = false"
    >
      <div v-if="!hasResults && !loading" class="text-center" style="margin: 3.125rem">
        <i
          :class="noResultsIcon"
          class="fa c-gray-700 faded"
          style="font-size: 6.25rem"
          aria-hidden="true"
        />
        <br />
        <span class="c-gray-700 f-13 m-t-20 iblock">
          {{ $t('No data available') }}
        </span>
      </div>
    </transition>
    <div v-show="hasResults" ref="canvasWrapper">
      <canvas ref="chart" width="200" height="80" aria-label="Analytics chart" role="img" />
    </div>
    <a ref="downloadImageLink" :href="base64image" download="chart.png" />
    <StatisticsToCsv v-slot="{ convertedString }" :entries="dataObject">
      <a ref="downloadCsvLink" :href="convertedString" />
    </StatisticsToCsv>
  </div>
</template>

<script>
/* eslint-disable no-param-reassign */
import { Chart } from 'chart.js/auto'
import StatisticsToCsv from '@/components/utilities/StatisticsToCsv.vue'
import { ChartTypes } from '@/utils/enum'
import { PropChartType } from '@/mixins/PropChartType'
import { getStyleColor } from '@/mixins/MethodColorMixin'
import { StatisticSeries } from '@/vue-prop-types/Statistics'

export default {
  components: {
    StatisticsToCsv,
  },

  mixins: [PropChartType],

  props: {
    dataObject: {
      type: StatisticSeries,
      default: () => new StatisticSeries(),
    },
    showGrid: { type: Boolean, default: false },
    unit: { type: String, default: 'currency' },
    loading: { type: Boolean, default: false },
  },

  data() {
    return {
      chart: null,
      base64image: '',
      showNotice: false,
      transitionInProgress: false,
    }
  },

  computed: {
    yAxisLabels() {
      return this.dataObjectComputed?.yAxisLabels
    },
    dataSets() {
      return this.dataObjectComputed?.dataSets || []
    },
    dataPointValues() {
      return this.dataSets[0]?.dataPoints
    },
    hasResults() {
      return this.verticalSumOfDataPoints.some((d) => d > 0)
    },
    yValues() {
      return this.hasResults ? this.dataPointValues : []
    },
    verticalSumOfDataPoints() {
      const dataPointsArrays = this.dataSets?.map((dataSet) => dataSet.dataPoints)
      return dataPointsArrays.reduce((r, a) => {
        a.forEach((b, i) => {
          r[i] = (r[i] || 0) + b
        })
        return r
      }, [])
    },
    max() {
      switch (this.chartType) {
        case ChartTypes.StackedPercentage:
          return 100
        case ChartTypes.Stacked:
          return Math.max(...this.verticalSumOfDataPoints) * 1.14
        default:
          return (
            Math.max(...(this.dataSets?.map((dataSet) => Math.max(...dataSet.dataPoints)) ?? [0])) *
            1.14
          )
      }
    },
    showLabels() {
      return (
        this.chartType !== ChartTypes.Doughnut &&
        this.dataSets.length === 1 &&
        !this.showGrid &&
        this.chartType !== ChartTypes.Line
      )
    },
    borderWidth() {
      return this.chartType === ChartTypes.Line ? 4 : 1
    },
    noResultsIcon() {
      switch (this.chartType) {
        case ChartTypes.Line:
          return 'fa-line-chart'
        case ChartTypes.Bar:
          return 'fa-bar-chart'
        case ChartTypes.Stacked:
          return 'fa-bar-chart'
        case ChartTypes.StackedPercentage:
          return 'fa-bar-chart'
        case ChartTypes.Doughnut:
          return 'fa-pie-chart'
        default:
          return 'fa-pie-chart'
      }
    },
    showLegend() {
      return this.dataSets.length > 1 || this.chartType === ChartTypes.Doughnut
    },
    dataObjectComputed() {
      // We need to do some modification on our data for it to work as doughnut data.
      // If you see the data as a matrix this is similar to collapsing our columns ie NxM -> N
      // Basically what this means is that we have to convert our StatisticSeries with multiple DataSets into
      // a new StatisticSeries with a single DataSet with all the combined data in it
      if (this.chartType === ChartTypes.Doughnut) {
        return new StatisticSeries({
          labels: this.dataObject.dataSets.map((set) => set.legend),
          dataSets: [
            {
              color: this.dataObject.dataSets.map((set) => set.color),
              dataPoints: this.dataObject.dataSets.map((set) => ({
                value: set.reduce((acc, val) => acc + val.value, 0),
              })),
            },
          ],
        })
      }
      return this.dataObject
    },
  },

  watch: {
    dataSets: {
      deep: true,
      handler() {
        this.redraw()
      },
    },
    labels() {
      this.redraw()
    },
    showGrid() {
      this.redraw(true)
    },
    chartType() {
      this.redraw(true)
    },
    loading() {
      return !this.loading && !this.hasResults
    },
    hasResults() {
      return !this.loading && !this.hasResults
    },
  },

  mounted() {
    this.registerChartPlugin()
    if (this.yValues?.length > 1) {
      this.redraw()
    }
  },

  methods: {
    updateAspectRatio() {
      if (this.transitionInProgress) return
      function paddingOffset(el) {
        const styles = window.getComputedStyle(el)
        return (
          parseInt(styles.getPropertyValue('padding-top'), 10) +
          parseInt(styles.getPropertyValue('padding-bottom'), 10)
        )
      }

      function elOffset(el) {
        if (el) {
          return el.clientHeight
        }
        return 0
      }

      function childrenOffset(el, target) {
        return (
          [].slice
            .call(el.children) // Converts html collection to array
            // Gets the height of all children that we are not decedents off
            .reduce((acc, child) => (child === target ? acc : acc + elOffset(child)), 0)
        )
      }

      const parentNode = this.$refs.container?.parentNode
      if (!parentNode) return // Guard statement
      const grandParent = parentNode?.parentNode
      if (!grandParent) return // Guard statement
      // Should be part of most browsers but check for it here
      if (typeof ResizeObserver !== 'undefined' && !this.resizeWatcher) {
        this.resizeWatcher = new ResizeObserver(this.updateAspectRatio)
        this.resizeWatcher.observe(grandParent)
      }

      const vOffset =
        paddingOffset(grandParent) +
        paddingOffset(parentNode) +
        childrenOffset(grandParent, parentNode) +
        childrenOffset(this.$refs.container, this.$refs.canvasWrapper)

      const aspectHeight = (80 / 200) * grandParent.clientWidth
      const newHeight = Math.max(aspectHeight, grandParent.clientHeight - vOffset)

      if (this.$refs.canvasWrapper) {
        this.$refs.canvasWrapper.style.minHeight = `${newHeight}px`
      }
    },
    translateToPercentages(values) {
      return values.map((value, index) => (value / this.verticalSumOfDataPoints[index]) * 100)
    },
    friendlyNumber(number) {
      const rounder = (num) => Math.round((num + Number.EPSILON) * 100) / 100
      const decSeparator = (1.1).toLocaleString().substring(1, 2)
      return `${rounder(number).toLocaleString()}${
        rounder(number) % 1 === 0 ? `${decSeparator}00` : ''
      }`
    },
    downloadImage(filename) {
      if (!this.base64image) {
        return null
      }
      this.$refs.downloadImageLink.download = filename
      this.$refs.downloadImageLink.click()
      return null
    },
    downloadCsv(filename) {
      this.$refs.downloadCsvLink.download = filename
      this.$refs.downloadCsvLink.click()
    },
    generateChartConfig() {
      Chart.defaults.font.family = 'Circular'
      Chart.defaults.font.size = 9

      return {
        type: [ChartTypes.Stacked, ChartTypes.StackedPercentage].includes(this.chartType)
          ? ChartTypes.Bar
          : this.chartType,
        data: {
          labels: this.yAxisLabels,
          datasets: this.dataSets.map((dataSet) => {
            const color = getStyleColor(dataSet.color)

            const set = {
              label: dataSet.legend || '',
              data:
                this.chartType === ChartTypes.StackedPercentage
                  ? [...this.translateToPercentages(dataSet.dataPoints)]
                  : [...dataSet.dataPoints],
              backgroundColor: color,
              borderColor: color,
              fill: false,
              lineTension: 0,
              borderWidth: [ChartTypes.Stacked, ChartTypes.StackedPercentage].includes(
                this.chartType,
              )
                ? 0
                : this.borderWidth,
              pointRadius: this.yValues.map((v, i) =>
                i === 0 || i === this.yValues.length - 1 ? 5 : 2,
              ),
            }
            if ([ChartTypes.Stacked, ChartTypes.StackedPercentage].includes(this.chartType)) {
              set.stack = '1'
            }
            return set
          }),
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            labelPlugin: this.showLabels,
            legend: {
              display: this.showLegend,
              position: 'top',
              align: 'start',
              labels: {
                padding: 15,
                fontSize: 12,
              },
              onHover(e) {
                e.native.target.style.cursor = 'pointer'
              },
            },
          },
          animation: {
            duration: 500,
            onComplete: () => {
              this.base64image = this.chart.toBase64Image()
            },
          },
          onHover(e) {
            const point = this.getElementsAtEventForMode(e)
            if (point.length) e.native.target.style.cursor = 'pointer'
            else e.native.target.style.cursor = 'default'
          },
          tooltips: {
            mode: 'index',
            titleAlign: 'center',
            bodyAlign: 'center',
            cornerRadius: 2,
            titleFontSize: 11,
            bodyFontSize: 11,
            titleFontColor: 'rgba(235,235,235,0.9)',
            bodyFontColor: 'rgba(255,255,255,1)',
            borderColor: 'rgba(75,75,75,0.9)',
            borderWidth: '0',
            displayColors: this.dataSets.length > 1,
            backgroundColor: 'rgba(55,55,55,0.9)',
            callbacks:
              this.chartType === ChartTypes.Doughnut
                ? {
                    label: (tooltipItem, data) => {
                      let label = data.labels[tooltipItem.index]
                      label +=
                        this.unit === 'currency' && this.chartType !== ChartTypes.StackedPercentage
                          ? ` ${userCurrencySymbol}`
                          : ''
                      const value = parseInt(data.datasets[0].data[tooltipItem.index], 10)
                      return `${label}: ${
                        this.unit === 'currency' ? this.friendlyNumber(value) : value
                      }`
                    },
                  }
                : {
                    label: (tooltipItem) => {
                      let label = ''
                      label +=
                        this.unit === 'currency' && this.chartType !== ChartTypes.StackedPercentage
                          ? ` ${userCurrencySymbol}`
                          : ''
                      label += ` ${
                        this.unit === 'currency'
                          ? this.friendlyNumber(parseFloat(tooltipItem.value, 10)) || '0'
                          : tooltipItem.value
                      }`
                      label += this.chartType === ChartTypes.StackedPercentage ? '%' : ''
                      return label
                    },
                  },
          },
          scales: {
            y: {
              display: this.chartType !== ChartTypes.Doughnut && this.showGrid,
              grid: {
                display: this.showGrid,
              },
              beginAtZero: true,
              ticks: {
                // max: this.max,
                display: true,
                precision: this.unit === 'currency' ? 2 : 0,
                callback: (value, index) => {
                  if (
                    this.chartType === ChartTypes.StackedPercentage &&
                    index !== 0 &&
                    value !== 0
                  ) {
                    return null
                  }
                  if (index === 0) {
                    // otherwise we have an additional tick at max value and looks odd
                    return this.chartType === ChartTypes.StackedPercentage ? '100%' : null
                  }
                  return this.unit === 'currency'
                    ? `${parseInt(value, 10).toLocaleString(currentUser.locale, {
                        minimumFractionDigits: 0,
                      })}`
                    : value
                },
              },
            },
            x: {
              stacked: [ChartTypes.Stacked, ChartTypes.StackedPercentage].includes(this.chartType),
              display: this.chartType !== ChartTypes.Doughnut,
              grid: {
                display: false,
              },
              beginAtZero: true,
            },
          },
        },
      }
    },
    registerChartPlugin() {
      Chart.register({
        id: 'labelPlugin',
        beforeDraw(chart) {
          const chartInstance = chart
          const { ctx } = chartInstance
          ctx.font = Chart.helpers.toFontString(
            9,
            Chart.defaults.font.style,
            Chart.defaults.font.family,
          )
          ctx.textAlign = 'center'
          ctx.textBaseline = 'bottom'
          ctx.fillStyle = 'rgba(55,55,55,0.9)'
          chart.data.datasets.forEach((dataset, i) => {
            const meta = chartInstance.controller.getDatasetMeta(i)
            meta.data.forEach((bar, index) => {
              const data = dataset.data[index]
              const display =
                data >= 1000000
                  ? `${(data / 1000000).toFixed(1)}M`
                  : data >= 1000
                    ? `${(data / 1000).toFixed(data / 1000 >= 10 ? 0 : 1)}K`
                    : data > 0
                      ? `${data}`
                      : ''
              ctx.fillText(display, bar._model.x, bar._model.y - 2)
            })
          })
        },
      })

      // https://stackoverflow.com/questions/42585861/chart-js-increase-spacing-between-legend-and-chart
      // https://stackoverflow.com/a/67723827
      Chart.register({
        id: 'paddingPlugin',
        beforeInit(chart) {
          // Get reference to the original fit function
          const originalFit = chart.legend.fit

          // Override the fit function
          chart.legend.fit = function fit() {
            originalFit.bind(chart.legend)()
            this.height += 10
          }
        },
      })
    },

    redraw(hard = false) {
      this.updateAspectRatio()
      const chartConfig = this.generateChartConfig()

      if (hard || !this.chart) {
        if (this.chart) {
          this.chart.destroy()
        }
        this.chart = new Chart(this.$refs.chart, chartConfig)
      }
    },
  },
}
</script>

<style scoped></style>
